import { useQuery, useMutation } from "@tanstack/react-query";
import { Icon } from "@terminus/dqh-web-ui";
import { QUERY_KEY } from "@/utils";
import {
  getCategoryItemByBusiness,
  getCategoryItemBySupplier,
} from "@/services";
import { CategoryItem } from "./item";
import "./index.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isPlainObject, size } from "lodash-es";
import { useCurrentPage } from "@/hooks";
import { Skeleton } from "antd";

export type CategoryProps = {
  height?: number;
  categoryIds?: string[];
  isExpand?: boolean;
  showIcon?: boolean;
  showSub?: boolean;
  isPosition?: boolean;
  isDetail?: boolean;
  title: string;
  type?: string;
};

export type CategoryItemType = {
  id: string;
  frontCateName: string;
  isLeaf: boolean;
  children?: CategoryItemType[];
  child?: CategoryItemType[];
  leaf?: boolean;
};

export const Category = (props: CategoryProps) => {
  const { currentPage } = useCurrentPage();
  const queryFn =
    currentPage === "business-opportunity"
      ? getCategoryItemByBusiness
      : getCategoryItemBySupplier;
  // 当前 hover 的根类目
  const [currentRoot, setCurrentRoot] = useState<Record<
    string,
    CategoryItemType[]
  > | null>();
  let timer: any | null = null;
  const {
    isExpand = false,
    showIcon = true,
    showSub = false,
    isPosition = false,
    isDetail = false,
    title,
    type = "supplier",
  } = props;

  const { data } = useQuery({
    queryKey: [QUERY_KEY.getCategoryItemByBusiness, currentPage],
    queryFn,
    staleTime: Infinity,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleRootEnter = useCallback(async (rows: CategoryItemType[]) => {
    if (timer) {
      clearTimeout(timer);
    }
    try {
      // // 并行发起所有请求
      // const requests = rows.map((item) => fetchSecondItem(item));
      // // 等待所有请求完成
      // const results = await Promise.all(requests);
      const feedback = {};
      rows.forEach((item) => {
        // @ts-ignore
        feedback[item.id!] = item;
      });

      // 处理结果，将每个结果与原始数据关联
      // @ts-ignore
      setCurrentRoot(feedback);
      // 更新状态或进行其他操作
    } catch (error) {
      console.error("Failed to fetch second items:", error);
      // 错误处理
    }
  }, []);

  const { data: _categoryData = [] } = data || {};

  const categoryData = useMemo(() => {
    return size(_categoryData) <= 16
      ? _categoryData
      : _categoryData.slice(0, 16);
  }, [_categoryData]);

  function handleRootLeave() {
    timer = setTimeout(() => {
      setCurrentRoot(null);
    }, 1000);
  }

  const positionStyle = isPosition
    ? {
        position: "absolute",
        zIndex: 99,
        top: `${Number(0)}px`,
        left: `${Number(0)}px`,
      }
    : null;
  let isExpandTemp = isExpand;
  let showSubTemp = showSub; // 直接展示子菜单
  if (isPosition) {
    isExpandTemp = true;
    // 悬浮模式，子类目不能自动展开
    showSubTemp = false;
  }

  const spliCategoryData = useMemo(() => {
    return categoryData.reduce(
      (rows: CategoryItemType[][], item: CategoryItemType, index: number) => {
        if (index % 2 === 0) {
          rows.push([item]); // 每两个元素分为一组
        } else {
          rows[rows.length - 1].push(item);
        }
        return rows;
      },
      []
    );
  }, [categoryData]);

  useEffect(() => {
    // 获取元素父节点
    // @ts-ignore
    const parentNode = document.getElementById("comp-nav-category").parentNode;
    if (parentNode) {
      if (isPosition) {
        // @ts-ignore
        parentNode.style.position = "relative";
      } else {
        // @ts-ignore
        parentNode.style.position = null;
      }
    }
  }, [isPosition]);

  return (
    <div
      style={{
        background: "#fff",
        height: 212,
        borderRadius: 4,
      }}
    >
      <div
        // className={`comp-nav-category ${isExpandTemp ? 'expand' : ''}`}
        className="comp-nav-category expand"
        // @ts-ignore
        style={positionStyle}
        onMouseLeave={handleRootLeave}
        id="comp-nav-category"
      >
        {
          spliCategoryData.length ?<>
                  {!isPosition && (
          <div className="nav-category-header" style={{ marginBottom: 8 }}>
            <Icon name="zuoduiqi2align-left-two" />
            <span className="nav-category-title">{title}</span>
          </div>
        )}
        <div
          className={`nav-category-body ${
            isPosition && "nav-category-body-position"
          } ${isDetail && "nav-category-body-is-Detail"}`}
          style={{ height: 248 }}
        >
          {!categoryData
            ? null
            : spliCategoryData.map(
                (row: CategoryItemType[], rowIndex: number) => {
                  return (
                    <div
                      key={rowIndex}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                      className="nav-category-row"
                      onMouseEnter={() => handleRootEnter(row)}
                    >
                      {row.map((item, rowIndex) => {
                        const { id } = item;
                        return (
                          <div
                            key={id}
                            className={
                              showSubTemp
                                ? "nav-category-item-show-wrap"
                                : "nav-category-item-wrap"
                            }
                          >
                            <CategoryItem
                              cutwords={false}
                              hasIcon={rowIndex % 2 === 0}
                              category={item}
                              showSub={showSubTemp}
                              level="first"
                            />

                            {rowIndex % 2 === 0 && (
                              <span
                                style={{
                                  fontSize: 14,
                                  color: "#D2D2D2",
                                  margin: "0 8px",
                                }}
                              >
                                /
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
              )}
        </div>
        <div className="nav-category-detail-mid"></div>
        {!showSubTemp && size(currentRoot) ? (
          <div className="nav-category-detail" style={{ height: 306 }}>
            {isPlainObject(currentRoot) &&
              Object.values(currentRoot!).map(
                // @ts-ignore
                (_detailItem: CategoryItemType) => {
                  const detailItem = _detailItem || {};
                  const { leaf: isLeaf, child: children } = detailItem;
                  return (
                    <div
                      onMouseEnter={() => clearTimeout(timer)}
                      key={detailItem.id}
                      className="nav-category-detail-item"
                    >
                      <CategoryItem
                        className="nav-category-detail-parent"
                        type={"outer"}
                        category={detailItem}
                        cutwords={false}
                        level="first"
                      />
                      <span className="nav-category-detail-list">
                        {!isLeaf &&
                          size(children) > 0 &&
                          children!.map((childItem) => {
                            const { id } = childItem;
                            return (
                              <CategoryItem
                                key={id}
                                type={"outer"}
                                className="nav-category-detail-child"
                                cutwords={false}
                                category={childItem}
                                level="second"
                                parent={detailItem}
                              />
                            );
                          })}
                      </span>
                    </div>
                  );
                }
              )}
          </div>
        ) : null}</>:<Skeleton.Input active style={{width:196,height:290}} />
        }

      </div>
    </div>
  );
};
